import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classes from './PreviewFile.module.css';
import * as pdfjs from 'pdfjs-dist';

export default function PreviewFilePDFPage({ pdf, pageNum, zoom, onRender }) {
    const canvasRef = useRef(null);
    const textContentRef = useRef(null);
    const [dimensions, setDimensions] = useState({
        width: 612,
        height: 792,
    });

    useEffect(() => {
        let cancelled = false;
        let renderTask = null;

        const renderPage = async () => {
            try {
                const page = await pdf.getPage(pageNum);
                if (cancelled) return;

                const canvas = canvasRef.current;
                if (!canvas) return;

                const context = canvas.getContext('2d');

                const scale = window.devicePixelRatio || 1;
                const viewport = page.getViewport({ scale });
                if (cancelled) return;

                const dimensions = {
                    width: viewport.width / scale,
                    height: viewport.height / scale,
                };
                setDimensions(dimensions);

                canvas.height = viewport.height;
                canvas.width = viewport.width;

                renderTask = page.render({
                    canvasContext: context,
                    viewport,
                });

                await renderTask.promise;
                if (cancelled) return;

                onRender(dimensions);
            } catch (error) {
                if (cancelled || error?.name === 'RenderingCancelledException') return;
                // PDF document destroyed mid-render — ignore
                if (error?.message?.includes('sendWithPromise')) return;
                throw error;
            }
        };
        renderPage();

        return () => {
            cancelled = true;
            if (renderTask) {
                renderTask.cancel();
            }
        };
    }, [pdf, pageNum]);

    useEffect(() => {
        let cancelled = false;
        let textLayerTask = null;

        const renderTextLayer = async () => {
            try {
                const page = await pdf.getPage(pageNum);
                if (cancelled) return;

                const textContent = await page.getTextContent();
                if (cancelled || !textContentRef.current) return;

                textContentRef.current.innerHTML = '';
                const viewport = page.getViewport({ scale: zoom });
                textContentRef.current.style.setProperty('--scale-factor', viewport.scale);

                textLayerTask = pdfjs.renderTextLayer({
                    textContent,
                    container: textContentRef.current,
                    viewport,
                });

                await (textLayerTask.promise || textLayerTask);
            } catch (error) {
                if (cancelled || error?.name === 'RenderingCancelledException') return;
                if (error?.message?.includes('sendWithPromise')) return;
                throw error;
            }
        };
        renderTextLayer();

        return () => {
            cancelled = true;
            if (textLayerTask?.cancel) {
                textLayerTask.cancel();
            }
        };
    }, [pdf, pageNum, zoom]);

    return (
        <div className={classes.page}>
            <canvas
                style={{
                    width: `${dimensions.width * zoom}px`,
                    height: `${dimensions.height * zoom}px`,
                }}
                ref={canvasRef}
            />

            <div className={classes.textContent} ref={textContentRef} />
        </div>
    );
}

PreviewFilePDFPage.propTypes = {
    pdf: PropTypes.object.isRequired,
    pageNum: PropTypes.number.isRequired,
    zoom: PropTypes.number.isRequired,
    onRender: PropTypes.func.isRequired,
};
